import type { MenuEntry } from '../ui/menu';

/**
 * The sizes people actually start from, in the groups they think of them in.
 *
 * One list, every consumer: the artboard tool's size menu, the quick bar's size menu, the
 * title menu's Artboards ▸ New, and the page panel. A document unit is a point, so print
 * sizes are their real millimetres and screen sizes are their real pixels.
 *
 * Named for what they are, not for whose app they are for: "Portrait post", not a
 * platform's trademark. Ids are stored on artboards (`preset`), so an id never changes;
 * add a new one instead.
 */

const MM = 72 / 25.4;
const IN = 72;

/** A size as the world states it, and in points. */
const mm = (w: number, h: number) => ({ width: w * MM, height: h * MM, says: `${w} × ${h} mm` });
const inch = (w: number, h: number) => ({ width: w * IN, height: h * IN, says: `${w} × ${h} in` });
const px = (w: number, h: number) => ({ width: w, height: h, says: `${w} × ${h} px` });

export const ARTBOARD_PRESETS = [
  // Paper
  { id: 'a3', group: 'Paper', name: 'A3', ...mm(297, 420) },
  { id: 'a4', group: 'Paper', name: 'A4', ...mm(210, 297) },
  { id: 'a5', group: 'Paper', name: 'A5', ...mm(148, 210) },
  { id: 'a6', group: 'Paper', name: 'A6', ...mm(105, 148) },
  { id: 'letter', group: 'Paper', name: 'Letter', ...inch(8.5, 11) },
  { id: 'legal', group: 'Paper', name: 'Legal', ...inch(8.5, 14) },
  { id: 'tabloid', group: 'Paper', name: 'Tabloid', ...inch(11, 17) },
  // Print
  { id: 'card', group: 'Print', name: 'Business card', ...mm(85, 55) },
  { id: 'card-us', group: 'Print', name: 'Business card (US)', ...inch(3.5, 2) },
  { id: 'postcard', group: 'Print', name: 'Postcard', ...inch(6, 4) },
  { id: 'flyer', group: 'Print', name: 'Flyer', ...inch(5.5, 8.5) },
  { id: 'poster', group: 'Print', name: 'Poster', ...mm(420, 594) },
  { id: 'poster-us', group: 'Print', name: 'Poster (US)', ...inch(18, 24) },
  // Social
  { id: 'post', group: 'Social', name: 'Square post', ...px(1080, 1080) },
  { id: 'portrait-post', group: 'Social', name: 'Portrait post', ...px(1080, 1350) },
  { id: 'story', group: 'Social', name: 'Story', ...px(1080, 1920) },
  { id: 'landscape-post', group: 'Social', name: 'Landscape post', ...px(1200, 630) },
  { id: 'wide-post', group: 'Social', name: 'Wide post', ...px(1600, 900) },
  { id: 'pin', group: 'Social', name: 'Pin', ...px(1000, 1500) },
  { id: 'thumbnail', group: 'Social', name: 'Video thumbnail', ...px(1280, 720) },
  { id: 'banner', group: 'Social', name: 'Profile banner', ...px(1500, 500) },
  // Screen
  { id: 'phone', group: 'Screen', name: 'Phone', ...px(393, 852) },
  { id: 'phone-large', group: 'Screen', name: 'Phone (large)', ...px(430, 932) },
  { id: 'tablet', group: 'Screen', name: 'Tablet', ...px(834, 1194) },
  { id: 'laptop', group: 'Screen', name: 'Laptop', ...px(1280, 832) },
  { id: 'desktop', group: 'Screen', name: 'Desktop', ...px(1440, 1024) },
  { id: 'watch', group: 'Screen', name: 'Watch', ...px(208, 248) },
  // Presentation
  { id: 'slide', group: 'Presentation', name: 'Slide 16:9', ...px(1920, 1080) },
  { id: 'slide-4-3', group: 'Presentation', name: 'Slide 4:3', ...px(1024, 768) },
] as const;

export type ArtboardPreset = (typeof ARTBOARD_PRESETS)[number]['id'];
export type PresetInfo = (typeof ARTBOARD_PRESETS)[number];

export const PRESET_GROUPS = ['Paper', 'Print', 'Social', 'Screen', 'Presentation'] as const;

/** A preset's size, or turned on its side: width and height swapped. */
export function sizeOf(preset: PresetInfo, turned = false): { width: number; height: number } {
  const width = Math.round(preset.width);
  const height = Math.round(preset.height);
  return turned ? { width: height, height: width } : { width, height };
}

/** Which preset a size is, either way up — so an A4 turned on its side still says A4. */
export function presetOfSize(width: number, height: number): { preset: PresetInfo; turned: boolean } | null {
  for (const preset of ARTBOARD_PRESETS) {
    const w = Math.round(preset.width);
    const h = Math.round(preset.height);
    if (Math.abs(w - width) < 0.5 && Math.abs(h - height) < 0.5) return { preset, turned: false };
    if (w !== h && Math.abs(h - width) < 0.5 && Math.abs(w - height) < 0.5) return { preset, turned: true };
  }
  return null;
}

/** "A4", "A4 landscape", or the bare numbers: how a size is named on screen. */
export function nameOfSize(width: number, height: number): string {
  const found = presetOfSize(width, height);
  if (!found) return `${Math.round(width)} × ${Math.round(height)}`;
  return found.turned ? `${found.preset.name} ${width > height ? 'landscape' : 'portrait'}` : found.preset.name;
}

/** A preset's size as its own unit states it, the given way up: "297 × 210 mm". */
export function saysWayUp(preset: PresetInfo, landscape: boolean): string {
  const naturally = preset.width > preset.height;
  if (preset.width === preset.height || naturally === landscape) return preset.says;
  return preset.says.replace(/^([\d.]+) × ([\d.]+)/, '$2 × $1');
}

/**
 * A preset's size the same way up as a board already is — choosing A4 for a landscape
 * board gives A4 landscape. A square board takes the preset as it comes.
 */
export function sizedLike(preset: PresetInfo, width: number, height: number): { width: number; height: number } {
  const long = Math.round(Math.max(preset.width, preset.height));
  const short = Math.round(Math.min(preset.width, preset.height));
  if (Math.abs(width - height) < 0.5) return sizeOf(preset);
  return width > height ? { width: long, height: short } : { width: short, height: long };
}

/**
 * The presets as a menu, a header per group, the size set right where a shortcut goes.
 * `checked` names the one to tick; `turned` turns every size shown.
 */
export function presetMenu(onPick: (preset: PresetInfo) => void, checked?: string | null, turned = false): MenuEntry[] {
  const out: MenuEntry[] = [];
  PRESET_GROUPS.forEach((group, index) => {
    if (index > 0) out.push({ kind: 'separator', id: `sep-${group}` });
    out.push({ kind: 'header', id: `head-${group}`, label: group });
    for (const preset of ARTBOARD_PRESETS.filter((one) => one.group === group)) {
      out.push({
        id: preset.id,
        label: preset.name,
        shortcut: turned ? preset.says.replace(/^([\d.]+) × ([\d.]+)/, '$2 × $1') : preset.says,
        checked: preset.id === checked,
        onSelect: () => onPick(preset),
      });
    }
  });
  return out;
}
